import type {
  ContinuousScrollerSort,
  DataGridColumnSettings,
  JsonData,
  JsonDataItem,
  JsonDataWrapper,
  OdinDataRetrievalOptions,
} from '@myosh/odin-components';
import { OdinDataSender } from '@myosh/odin-components';
import { get, isString, orderBy } from 'lodash';
import type { FieldFilters } from '../@types/common';
import type { GroupStructure } from '../@types/groups';
import { api } from '../redux/services/api';
import { groupApi } from '../redux/services/group';
import { moduleApi } from '../redux/services/module';
import { userApi } from '../redux/services/user';
import { appDispatch } from '../redux/store-accessors';
import { UserFields } from './user-config';

/**
 * A helper method to get only the form field values that have been changed by user interaction in a form.
 *
 * @param dirtyFields fields that have been changed
 * @param formData form data
 * @returns object with key, value pair
 */
export const getChangedFieldsData = <T extends object>(
  dirtyFields: Record<keyof Partial<T>, boolean>,
  formData: T
): Partial<T> => {
  const modifiedFields: Partial<T> = {};
  for (const fieldName in dirtyFields) {
    if (dirtyFields[fieldName] && Object.hasOwn(formData as object, fieldName)) {
      modifiedFields[fieldName] = formData[fieldName];
    }
  }
  return modifiedFields;
};

export const getGroups = async (
  subscriber: OdinDataSender<JsonDataWrapper>,
  options: OdinDataRetrievalOptions,
  isExternal?: boolean,
  valueField = 'value',
  textField = 'text'
) => {
  if (options.page === 1) {
    let finalFilter: FieldFilters | undefined = options?.fieldFilters;
    if (options?.fieldFilters?.text) {
      finalFilter = {
        ...finalFilter,
        name: options.fieldFilters.text,
      };
      delete finalFilter.text;
    }
    const dispatchResult = appDispatch(
      groupApi.endpoints.getGroups.initiate({
        filters: finalFilter,
        externalUserSignupRoles: isExternal ?? false,
      })
    );
    const result = await dispatchResult;

    if ('fulfilled' === result.status) {
      const comboboxOptions = (result.data as Array<GroupStructure>)
        .filter((item) => !item?.archived)
        .map((item) => {
          return {
            [valueField]: item.id,
            [textField]: item.caption,
          };
        });
      subscriber.sendData({ data: comboboxOptions, requestId: options.requestId });
    } else {
      subscriber.sendData();
    }
    dispatchResult.unsubscribe();
  } else {
    subscriber.sendData();
  }
};

export const getUsers = async (subscriber: OdinDataSender<JsonDataWrapper>, options: OdinDataRetrievalOptions) => {
  if (options.page && options.pageSize) {
    //Managers should be filtered by fullName that is included in `value` property of the `text` attribute
    const finalFilter: FieldFilters | undefined = options?.fieldFilters?.text
      ? { fullName: options?.fieldFilters?.text }
      : options?.fieldFilters;

    const dispatchResult = appDispatch(
      userApi.endpoints.getUsers.initiate({
        page: options.page,
        pageSize: options.pageSize,
        archivedUsers: false,
        sortedField: UserFields.fullName,
        filters: finalFilter,
      })
    );
    const result = await dispatchResult;

    if ('fulfilled' === result.status) {
      const comboboxOptions = result.data
        .filter((item) => !item.externalUser)
        .map((item) => {
          return {
            value: item.id,
            text: `${item.fullName}`,
          };
        });
      subscriber.sendData({ data: comboboxOptions, requestId: options.requestId });
    } else {
      subscriber.sendData();
    }
    dispatchResult.unsubscribe();
  }
};

export const getModuleForms = async (
  subscriber: OdinDataSender<JsonDataWrapper>,
  options: OdinDataRetrievalOptions
) => {
  const moduleId = options?.customProperties?.moduleId as number;

  if (options.page === 1 && options.pageSize && moduleId) {
    const dispatchResult = appDispatch(api.endpoints.moduleForms.initiate({ moduleId }));
    const result = await dispatchResult;

    if ('fulfilled' === result.status) {
      const comboboxOptions = result.data.result.forms?.map((item) => {
        return {
          value: item.id,
          text: item.caption,
        };
      });

      subscriber.sendData({
        data: comboboxOptions?.filter((item) => item?.value !== 0) ?? [],
        requestId: options.requestId,
      });
    } else {
      subscriber.sendData();
    }
    dispatchResult.unsubscribe();
  } else {
    subscriber.sendData();
  }
};

export const getFormWorkflowSteps = async (
  subscriber: OdinDataSender<JsonDataWrapper>,
  options: OdinDataRetrievalOptions
) => {
  const formId = options?.customProperties?.formId as number;

  if (options.page === 1 && options.pageSize && formId) {
    const dispatchResult = appDispatch(
      api.endpoints.getWorkflowSteps.initiate({ formId, filters: { archived: { value: false, comparison: '=' } } })
    );
    const result = await dispatchResult;

    if ('fulfilled' === result.status) {
      const comboboxOptions = result.data?.map((item) => {
        return {
          value: item.id,
          text: item.label,
        };
      });
      subscriber.sendData({ data: comboboxOptions ?? [], requestId: options.requestId });
    } else {
      subscriber.sendData();
    }
    dispatchResult.unsubscribe();
  } else {
    subscriber.sendData();
  }
};

export const getModules = async (subscriber: OdinDataSender<JsonDataWrapper>, options: OdinDataRetrievalOptions) => {
  if (options.page === 1 && options.pageSize) {
    const dispatchResult = appDispatch(moduleApi.endpoints.modules.initiate({ includeFormList: false }));
    const result = await dispatchResult;

    if ('fulfilled' === result.status) {
      const comboboxOptions = result.data?.map((item) => {
        return {
          value: item.id,
          text: item.caption,
        };
      });
      subscriber.sendData({ data: comboboxOptions ?? [], requestId: options.requestId });
    } else {
      subscriber.sendData();
    }
    dispatchResult.unsubscribe();
  } else {
    subscriber.sendData();
  }
};

export const resolveDashboardUrl = (systemHostname?: string) => {
  const configuredUrl = process.env.REACT_APP_DASHBOARD_URL;

  if (systemHostname && configuredUrl) {
    const url = new URL(configuredUrl);
    url.hostname = systemHostname;
    return url.toString();
  }

  return configuredUrl;
};

export const caseInsensitiveSortFunction = (
  data: JsonData,
  sorts: Array<ContinuousScrollerSort>,
  columns: Array<DataGridColumnSettings>
): JsonData => {
  const sortFields = sorts.map((sort) => {
    const column = columns.find((column) => column.field === sort.field);

    const pathParts: string[] = [column?.valuePath ?? sort.field];

    if (column?.sortPath) {
      pathParts.push(column.sortPath.replace(/^\./, ''));
    }

    const sortValuePath = pathParts.join('.');

    return (record: JsonDataItem) => {
      const sortValue = get(record, sortValuePath);
      if (isString(sortValue)) {
        return sortValue.toLowerCase();
      }
      return sortValue;
    };
  });

  const sortDirections = sorts.map((sort) => (sort.sortDirection === 0 ? 'asc' : 'desc'));

  return orderBy(data, sortFields, sortDirections);
};
